import {
    Box,
    Button,
    capitalize,
    Chip,
    Stack,
    useMediaQuery,
    useTheme,
    TextField,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {AnwesenheitDaten, AnwesenheitInfo} from "../../api";
import FilterBenutzerSelect from "../benutzer/FilterBenutzerSelect";
import {Field, Formik} from "formik";
import DataTable from "../data/DataTable";
import {
    useApproveMultipleAnwesenheiten,
    useGetBenutzer,
    useUpdateAnwesenheit,
} from "../../client/hooks";
import {useQuery, useQueryClient} from "react-query";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import Grid from "@mui/material/Grid2";
import UpdateAzSaldoAnwesenheitButton from "./UpdateAzSaldoAnwesenheitButton";
import {formatGermanNumber} from "../../utils/fomatters";
import useCustomFilters from "../data/useCustomFilters";
import StartEndFilter from "../StartEndFilter";

export type AnwesenheitenFilter = {
    search?: string;
    mitarbeiterId?: string;
    startDate?: string;
    endDate?: string;
    filtered?: string;
};

interface AnwesenheitProps {
    anwesenheitIds: string[];
    setAnwesenheitIds: (value: string[]) => void;
}

export type Props = Omit<DataRequest<AnwesenheitenFilter>, "filter"> &
    Partial<Pick<DataRequest<AnwesenheitenFilter>, "filter">>;

const tableName: string = "anwesenheiten"

export default function AnwesenheitenDataTable({...input}: Props) {
    const request = useDataRequest<AnwesenheitenFilter>({filter: {}, ...input});
    const [anwesenheitIds, setAnwesenheitIds] = useState<string[]>([]);

    const [filteredState, setFilteredState] = useState("");
    const [updateAzSaldo, setUpdateAzSaldo] = useState(false);

    return (
        <Stack spacing={2}>
            <FilterComp
                anwesenheitIds={anwesenheitIds}
                setAnwesenheitIds={setAnwesenheitIds}
                filteredStateFilter={filteredState}
                setFilteredState={setFilteredState}
                updateAzSaldo={updateAzSaldo}
                setUpdateAzSaldo={setUpdateAzSaldo}
                {...request}
            />
            <AnwesenheitenResults
                anwesenheitIds={anwesenheitIds}
                setAnwesenheitIds={setAnwesenheitIds}
                setFilteredState={setFilteredState}
                setUpdateAzSaldo={setUpdateAzSaldo}
                {...request}
            />
        </Stack>
    );
}

function FilterComp({
                        anwesenheitIds,
                        setAnwesenheitIds,
                        filteredStateFilter,
                        setFilteredState,
                        updateAzSaldo,
                        setUpdateAzSaldo,
                        ...request
                    }: AnwesenheitProps & {
    filteredStateFilter: string;
    setFilteredState: (value: string) => void;
    setUpdateAzSaldo: (value: boolean) => void;
    updateAzSaldo: boolean;
} & DataRequestState<AnwesenheitenFilter>) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<AnwesenheitenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            mitarbeiterId: undefined,
            startDate: "",
            endDate: "",
        }
    });

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const approve = useApproveMultipleAnwesenheiten();
    const queryClient = useQueryClient();
    const {t: translation} = useT();
    const [az_saldo, setAzSaldo] = useState("-");
    const ladeBenutzer = useGetBenutzer();
    const {data: benutzerData} = useQuery(
        ["azsaldo", currentCustomFilter.mitarbeiterId],
        () => ladeBenutzer(currentCustomFilter.mitarbeiterId as string),
        {
            enabled:
                !!currentCustomFilter.mitarbeiterId && currentCustomFilter.mitarbeiterId !== "_all" && currentCustomFilter.mitarbeiterId !== "",
            refetchOnWindowFocus: false,
        }
    );

    async function approveAnwesenheiten(anwesenheitsIds: Array<string>) {
        await approve(anwesenheitsIds).then(() => {
            queryClient.invalidateQueries([tableName]);
            setUpdateAzSaldo(true);
        });
    }

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            mitarbeiterId: currentCustomFilter.mitarbeiterId,
            startDate: currentCustomFilter.startDate,
            endDate: currentCustomFilter.endDate,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    useEffect(() => {
        if (currentCustomFilter.mitarbeiterId) {
            if (currentCustomFilter.mitarbeiterId === "_all") {
                setAzSaldo("-");
            } else {
                (async () => {
                    setAzSaldo(
                        benutzerData?.azSaldo?.toString().replace(".", ",") ?? "-"
                    );
                })();
            }
        }
        if (updateAzSaldo) {
            setUpdateAzSaldo(false);
        }
    }, [currentCustomFilter.mitarbeiterId, updateAzSaldo, benutzerData, setUpdateAzSaldo]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-anwesenheit");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "space-between",
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                            }}
                        >
                            <SearchField
                                value={currentCustomFilter.search}
                                onChange={(val) => setCustomField("search", val)}
                            />
                            <Formik
                                initialValues={{
                                    mitarbeiterId: currentCustomFilter.mitarbeiterId,
                                    startDate: currentCustomFilter.startDate,
                                    endDate: currentCustomFilter.endDate,
                                }}
                                onSubmit={() => {
                                }}
                            >
                                <>
                                    <StartEndFilter startDatum={currentCustomFilter.startDate}
                                                    endeDatum={currentCustomFilter.endDate}
                                                    setCustomField={setCustomField}
                                    />
                                    <Box sx={{ml: 2}}></Box>
                                    <Field
                                        component={FilterBenutzerSelect}
                                        name={"mitarbeiterId"}
                                        label={capitalize(translation("employee"))}
                                        setBenutzer={(val: any) => setCustomField("mitarbeiterId", val)}
                                    />
                                    <TextField
                                        sx={{ml: 2, width: "150px"}}
                                        size={"small"}
                                        value={az_saldo}
                                        disabled
                                        label={capitalize(translation("az-saldo"))}
                                    />
                                    <Grid size={{xs: 1, md: 1}}>
                                        <UpdateAzSaldoAnwesenheitButton
                                            title={az_saldo}
                                            selectedMitarbeiterId={currentCustomFilter.mitarbeiterId}
                                        ></UpdateAzSaldoAnwesenheitButton>
                                    </Grid>
                                </>
                            </Formik>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => approveAnwesenheiten(anwesenheitIds)}
                            >
                                {capitalize(translation("approve"))}
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate("/neu-anwesenheit")}
                            >
                                {capitalize(translation("create-new"))}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
}

function AnwesenheitenResults({
                                  anwesenheitIds,
                                  setAnwesenheitIds,
                                  setFilteredState,
                                  setUpdateAzSaldo,
                                  ...request
                              }: AnwesenheitProps & {
    setFilteredState: (value: string) => void;
    setUpdateAzSaldo: (value: boolean) => void;
} & DataRequestState<AnwesenheitenFilter>) {
    return (
        <Results
            anwesenheitIds={anwesenheitIds}
            setAnwesenheitIds={setAnwesenheitIds}
            request={request}
            setFilteredState={setFilteredState}
            setUpdateAzSaldo={setUpdateAzSaldo}
        />
    );
}

function Results({
                     setAnwesenheitIds,
                     request,
                     setFilteredState,
                     setUpdateAzSaldo,
                 }: AnwesenheitProps & {
    request: DataRequestState<AnwesenheitenFilter>;
    setFilteredState: (value: string) => void;
    setUpdateAzSaldo: (value: boolean) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadAnwesenheiten: DataLoader<AnwesenheitenFilter, AnwesenheitInfo> =
        useCallback(
            async (params) =>
                (await getApi()).anwesenheiten.sucheAnwesenheiten({
                    search: params.search,
                    mitarbeiterId: params.mitarbeiterId,
                    startDatum: params.startDate,
                    endeDatum: params.endDate,
                    filtered: params.filtered,
                    page: params.page,
                    limit: params.limit,
                    sort: params.sort,
                }),
            [getApi]
        );
    const updateAnwesenheit = useUpdateAnwesenheit();
    const queryClient = useQueryClient();
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadAnwesenheiten}
            name={tableName}
            // @ts-ignore
            checkboxSelection={true}
            disableRowSelectionOnClick={true}
            onRowSelectionModelChange={(newRowSelectionModel: any) => {
                setAnwesenheitIds(newRowSelectionModel);
            }}
            onRowClick={(row, event) => {
                //@ts-ignore
                if (event.target.attributes[2].value !== "arbeitszeitKonto") {
                    if (event.ctrlKey || event.metaKey) {
                        window.open("/anwesenheiten/" + row.id, "_blank");
                    } else {
                        navigate("/anwesenheiten/" + row.id);
                    }
                }
            }}
            processRowUpdate={(updatedRow: any) => {
                const newAnwesenheitData: AnwesenheitDaten = {
                    datum: updatedRow.datum,
                    mitarbeiterId: updatedRow.mitarbeiterId,
                    start: updatedRow.start,
                    ende: updatedRow.ende,
                    arbeitszeitKonto: updatedRow.arbeitszeitKonto,
                    arbeitszeitBrutto: updatedRow.arbeitszeitBrutto,
                    arbeitszeitNetto: updatedRow.arbeitszeitNetto,
                    bezahltePause: updatedRow.bezahltePause,
                    unbezahltePause: updatedRow.unbezahltePause,
                    fahrzeiten: updatedRow.fahrzeiten,
                    status: updatedRow.status,
                    brzUebertrag: updatedRow.brzUebertrag,
                };

                updateAnwesenheit(updatedRow.id, newAnwesenheitData).then(() => {
                    queryClient.invalidateQueries([tableName]);
                    setUpdateAzSaldo(true);
                });

                return updatedRow;
            }}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<AnwesenheitInfo>> {
    const {t: translation} = useT("anwesenheit");

    return useMemo(
        () => [
            {
                field: "name",
                headerName: capitalize(translation('anwesenheit-attributes-employee')),
                flex: 1,
            },
            {
                field: "datum",
                headerName: capitalize(translation('anwesenheit-attributes-date')),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        });
                    }
                    return "";
                },
            },
            {
                field: "start",
                headerName: capitalize(translation('anwesenheit-attributes-start')),
                filterable: false,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    }
                    return "";
                },
            },
            {
                field: "ende",
                headerName: capitalize(translation('anwesenheit-attributes-end')),
                filterable: false,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    }
                    return "";
                },
            },
            {
                field: "arbeitszeitBrutto",
                headerName: capitalize(translation('anwesenheit-attributes-paid-worktime')),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "arbeitszeitKonto",
                headerName: capitalize(translation('anwesenheit-attributes-az-account')),
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                editable: true,
                valueFormatter: formatGermanNumber,
            },
            {
                field: "brzUebertrag",
                headerName: capitalize(translation('anwesenheit-attributes-brz-overflow')),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "fahrzeiten",
                headerName: capitalize(translation('anwesenheit-attributes-drive-times')),
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "bezahltePause",
                headerName: capitalize(translation('anwesenheit-attributes-paid-pause')),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "unbezahltePause",
                headerName: capitalize(translation('anwesenheit-attributes-unpaid-pause')),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "arbeitszeitNetto",
                headerName: capitalize(translation('anwesenheit-attributes-net-worktime')),
                flex: 0.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                valueFormatter: formatGermanNumber,
            },
            {
                field: "status",
                headerName: capitalize(translation('anwesenheit-attributes-status')),
                flex: 1,
                renderCell: (params) => {
                    switch (params.value) {
                        case "OFFEN":
                            return (
                                <Chip
                                    icon={<SpellcheckIcon/>}
                                    title={"Offen"}
                                    color={"secondary"}
                                    label={"Offen"}
                                ></Chip>
                            );
                        case "ANGENOMMEN":
                            return (
                                <Chip
                                    icon={<PlaylistAddCheckCircleIcon/>}
                                    title={"Freigegeben"}
                                    color={"success"}
                                    label={"Freigegeben"}
                                ></Chip>
                            );
                    }
                },
            },
        ],
        [translation]
    );
}
